import React, { useState } from 'react';
import cx from 'classnames';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import { formatPrice } from 'utilities/money';

import { Col, Collapse, Grid, Row } from 'react-bootstrap';
import { DefaultButton, SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from "@magoosh/lib/icons";
import Confirmation from './confirmation';

import { AddOnOffer } from './types';

import HeadshotBrittny from './assets/admissions/headshot_brittny.jpg';
import HeadshotJen from './assets/admissions/headshot_jen.jpg';
import HeadshotLinnea from './assets/admissions/headshot_linnea.jpg';
import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';
import { paths } from 'config/path_helpers';
import { Cohort } from '@magoosh/pages/payment_flows/types';
import moment, { tz } from 'moment-timezone';
import { toSentence } from '../../../../utilities/formatting';
import { uniq } from 'lodash';
import { formatTimeRange, longTimeZoneName } from '../../../../utilities/time';

const CLASS_DETAILS = [
  {
    description: "Review key facts about GRE structure, timing, and scoring; learn test-taking strategies for getting the most points on each section; dig into each GRE question type; and discuss how to study effectively.",
    name: "Test Strategy"
  },
  {
    description: "Review major Arithmetic topics, explore how these topics are tested on the GRE, and learn and practice GRE Math strategies.",
    name: "Arithmetic"
  },
  {
    description: "Explore how to approach GRE Reading Comprehension questions, practice skimming passages, and learn strategies for each Reading Comprehension question type.",
    name: "Reading Comprehension"
  },
  {
    description: "Review major Algebra topics, explore how these topics are tested on the GRE, and continue learning and practicing GRE Math strategies.",
    name: "Algebra"
  },
  {
    description: "Explore how to approach GRE Paragraph Argument questions, practice breaking down arguments, and learn strategies for each Paragraph Argument question type.",
    name: "Paragraph Argument"
  },
  {
    description: "Review major Geometry topics, explore how these topics are tested on the GRE, and continue learning and practicing GRE Math strategies.",
    name: "Geometry"
  },
  {
    description: "Explore how to approach GRE Text Completion and Sentence Equivalence questions, practice breaking down sentences, and learn strategies for each question type.",
    name: "Text Completion & Sentence Equivalence"
  },
  {
    description: "Review major Statistics topics, explore how these topics are tested in GRE Data Interpretation sets, and continue learning and practicing GRE Math strategies.",
    name: "Statistics & Data Interpretation"
  }
];

const timeZone = tz.guess();

const LiveClassesAddOn: React.FC<AddOnOffer> = (props) => {
  const { discountedPrice, fullPrice } = props.price;

  const showCohortOptions = props.orderItem.purchasable.cohortOptions.length > 0;

  const [selectedCohort, setSelectedCohort] = useState(props.orderItem.purchasable.cohortOptions[0]);

  const { setModal } = useModalContext();

  const handleAddOnPurchaseClicked = () => {
    if (props.paymentDetails) {
      setModal({
        header: 'Add Live Classes',
        body: (
          <Confirmation
            addOnName="Live Classes"
            handleClose={() => setModal(null)}
            cohortId={selectedCohort.id}
            successMessage="Navigate to the Classes tab on your Magoosh dashboard to access each class and its associated materials."
            {...props} />
        )
      });
    } else {
      window.location.href = `${paths.subscriptions(props.orderItem.purchasable.planSlug)}?coupon=${props.price.couponCode}`;
    }
  };

  const handleCohortChanged = (newValue) => {
    const cohort = props.orderItem.purchasable.cohortOptions.find((cohort) => cohort.id == newValue);
    setSelectedCohort(cohort);
  }

  const cohortDisplayName = (cohort: Cohort, timeZone: string) => {
    if (cohort.onDemand) {
      return (
        <span>
          <strong>On Demand</strong>: Watch classes at your own pace and time
        </span>
      );
    }

    const startDate = moment.unix(cohort.liveClassSessions[0].startTime).tz(timeZone);
    const endDate = moment
      .unix(cohort.liveClassSessions[cohort.liveClassSessions.length - 1].startTime)
      .tz(timeZone);

    const formattedStartDate = startDate.format('MMM. DD');
    const formattedEndDate = endDate.format('MMM. DD');

    const scheduleSummary = toSentence(
      uniq(
        cohort.liveClassSessions.map((session) => {
          const startTime = moment.unix(session.startTime).tz(timeZone);
          const endTime = moment.unix(session.endTime).tz(timeZone);
          return formatTimeRange(startTime, endTime, 'ddd: ');
        })
      ).slice(0, 2)
    );

    return (
      <span>
        <strong>
          {formattedStartDate} - {formattedEndDate},
        </strong>{' '}
        {scheduleSummary}
      </span>
    );
  };

  return (
    <Grid className="u-padding-T-m u-padding-B-xl">
      <Row>
        <Col md={6} mdOffset={3}>
          <h4 className="text-gray">One-Time Offer</h4>
          <h2 className="u-margin-T-n">
            Add Live Classes for{' '}
            {
              discountedPrice !== fullPrice && (
                <>
                  <span style={{textDecoration: 'line-through'}}>
                    {formatPrice(fullPrice, 'usd', true)}
                  </span>
                  {' '}
                </>
              )
            }
            {formatPrice(discountedPrice, 'usd', true)}!
          </h2>
        </Col>
      </Row>

      <Row>
        <Col md={6} mdOffset={3}>
          <hr className="u-margin-T-n" />

          <p>
            Get extra support and accountability in a dynamic online classroom setting directed by one of our 99th-percentile instructors.
          </p>

          {
            discountedPrice !== fullPrice && (
              <p>
                <strong>Plus - if you sign up now, you’ll get a big discount</strong> (regular price
                is {formatPrice(fullPrice, 'usd', true)})!
              </p>
            )
          }

          {showCohortOptions && (
            <div>
              <legend className={style.noMargin}>
                <Row>
                  <Col sm={9}>Select Your Live Class Cohort</Col>
                  <Col sm={9}>
                    <a style={{ fontSize: '16px' }} href="/about-live-classes#schedule" target="_blank">
                      Full Cohort Schedule Details
                    </a>
                  </Col>
                </Row>
              </legend>
              <p className="help-block">
                Showing class times in your time zone: {longTimeZoneName(moment().tz(timeZone))}
              </p>

              <fieldset className="radio">
                <div className="controls radio_buttons form-group">
                  <div>
                    {props.orderItem.purchasable.cohortOptions.map((cohort) => ({
                      value: cohort.id,
                      displayName: cohortDisplayName(cohort, timeZone)
                    })).map((option, index) => (
                      <div className={cx('radio', null)} key={index}>
                        <label className="choice">
                          <input
                            type="radio"
                            name="cohortId"
                            value={option.value}
                            checked={selectedCohort.id === option.value}
                            onChange={() => handleCohortChanged(option.value)}
                          />
                          {option.displayName}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </fieldset>
            </div>
          )}

          <div className={cx('d-flex u-margin-V-l', style.ctas)}>
            <SecondaryButton onClick={handleAddOnPurchaseClicked}>
              Yes, Add <span className="hidden-xs">Live Classes</span> for {formatPrice(discountedPrice, 'usd', true)}
            </SecondaryButton>

            {props.nextPath && <DefaultButton className="u-margin-L-s" href={props.nextPath}>
              No Thanks
            </DefaultButton>}
          </div>

          <div className="border-radius-all u-margin-V-l u-padding-A-m" style={{backgroundColor: '#F5EEFC'}}>
            <p className="text-uppercase"><strong>16 HOURS OF LIVE INSTRUCTION</strong></p>

            {
              CLASS_DETAILS.map((classDetail, idx) => (
                <ClassDetail key={idx} classNumber={idx + 1} {...classDetail} />
              ))
            }
          </div>

          <div className="u-margin-T-xl">
            <div className="u-margin-B-s">
              Each live class cohort features limited class sizes, an engaging expert instructor, targeted homework assignments, and more! Learn more about Live Classes <a href="/about-live-classes">here</a>.
            </div>
          </div>

          <div className="d-flex u-margin-T-xl">
            <div>
              <span style={{color: colors.statusYellow}}>
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
              </span>

              <p>
                "Great instruction and pace. Loved how [the instructor] tiered the instruction and gave detailed explanations of how/why answers were incorrect."
              </p>
              <p>
                - GRE Student, December 2021
              </p>
            </div>
          </div>

          <div className="d-flex">
            <div>
              <span style={{color: colors.statusYellow}}>
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
              </span>

              <p>
                "All the concepts were cleared very quickly and the session was highly interactive, making learning even more fun!!!"
              </p>
              <p>
                - GRE Student, January 2022
              </p>
            </div>
          </div>

          <div className="d-flex">
            <div>
              <span style={{color: colors.statusYellow}}>
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
                <FontAwesomeIcon icon="star" />
              </span>

              <p>
                "I love that it is interactive and I’m able to ask questions as needed to clarify the topic being discussed and any associated questions I have. I also enjoy hearing the questions and the answers that others pose – those that I may not have thought of but are very helpful in learning."
              </p>
              <p>
                - GRE Student, January 2022
              </p>
            </div>
          </div>

          <div className="u-margin-T-xl">
            <div className="u-margin-B-s">
              <strong><u>Meet Your Instructors</u></strong>
            </div>

            <div className="d-flex justify-content-between">

              <div>
                <img
                  alt="Brittny Photo"
                  className="img-circle"
                  src={HeadshotBrittny}
                  style={{objectFit: 'cover', height: 100, width: 100}} />
                <div className="u-margin-T-s small text-center">Brittny Stringfield</div>
              </div>

              <div>
                <img
                  alt="Linnea Photo"
                  className="img-circle"
                  src={HeadshotLinnea}
                  style={{objectFit: 'cover', height: 100, width: 100}} />
                <div className="u-margin-T-s small text-center">Linnea Newman</div>
              </div>

              <div>
                <img
                  alt="Jen Photo"
                  className="img-circle"
                  src={HeadshotJen}
                  style={{objectFit: 'cover', height: 100, width: 100}} />
                <div className="u-margin-T-s small text-center">Jen Jurgens</div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

interface ClassDetailProps {
  classNumber: number;
  description: string;
  name: string;
}

const ClassDetail: React.FC<ClassDetailProps> = ({ classNumber, description, name }) => {
  const [isExpanded, setIsExpanded] = React.useState(classNumber === 1);

  return (
    <div className="u-margin-T-s">
      <div onClick={() => setIsExpanded(!isExpanded)} style={{cursor: 'pointer'}}>
        <strong>
          <span className="u-margin-R-xs" style={{color: colors.brandFuchsia}}>
            <span className="u-margin-R-xxs">
              <FontAwesomeIcon icon={isExpanded ? 'minus' : 'plus'} />
            </span>
            <span>
              Class {classNumber}.
            </span>
          </span>
          <span>
            {name}
          </span>
        </strong>
      </div>

      <Collapse in={isExpanded}>
        <div>{description}</div>
      </Collapse>
    </div>
  );
};

const LiveClassesAddOnWithProvider: React.FC<AddOnOffer> = (props) => {
  return (
    <ModalProvider>
      <LiveClassesAddOn {...props} />
    </ModalProvider>
  );
};

export default LiveClassesAddOnWithProvider;
